import React, { useState, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import { Line, Bar, Pie } from "react-chartjs-2";
import { Grid, Paper, Typography, Box, Button, MenuItem, FormControl, Select, TextField } from "@mui/material";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  ChartTooltip,
  Legend,
  Filler,
  zoomPlugin
);

// Fix broken Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const DashboardCharts = () => {
  const [timeRange, setTimeRange] = useState("custom");
  const [regionFilter, setRegionFilter] = useState("all");
  const [productCategoryFilter, setProductCategoryFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ start: "2023-01-01", end: "2023-12-31" });

  // Default chart data (fallback)
  const defaultLineChartData = {
    labels: ["January", "February", "March", "April", "May", "June"],
    datasets: [
      {
        label: "Revenue",
        data: [5000, 10000, 7500, 15000, 12500, 20000],
        borderColor: "#1E88E5",
        backgroundColor: "rgba(30, 136, 229, 0.3)",
        borderWidth: 2,
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6,
        fill: true,
      },
    ],
  };

  const defaultBarChartData = {
    labels: ["Product A", "Product B", "Product C", "Product D"],
    datasets: [
      {
        label: "Sales",
        data: [300, 400, 200, 500],
        backgroundColor: ["#FF7043", "#66BB6A", "#42A5F5", "#AB47BC"],
        hoverBackgroundColor: ["#FF8A65", "#81C784", "#64B5F6", "#CE93D8"],
        borderWidth: 2,
      },
    ],
  };

  const defaultPieChartData = {
    labels: ["New Customers", "Returning Customers"],
    datasets: [
      {
        data: [60, 40],
        backgroundColor: ["#26C6DA", "#EF5350"],
        hoverBackgroundColor: ["#4DD0E1", "#FF6F61"],
        borderWidth: 1,
      },
    ],
  };

  const defaultMapMarkers = [
    { position: [37.7749, -122.4194], label: "San Francisco, $10,000 Revenue" },
    { position: [40.7128, -74.006], label: "New York, $15,000 Revenue" },
    { position: [34.0522, -118.2437], label: "Los Angeles, $8,000 Revenue" },
  ];

  // State for fetched data
  const [lineChartData, setLineChartData] = useState(defaultLineChartData);
  const [barChartData, setBarChartData] = useState(defaultBarChartData);
  const [pieChartData, setPieChartData] = useState(defaultPieChartData);
  const [mapMarkers, setMapMarkers] = useState(defaultMapMarkers);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const token = localStorage.getItem("token"); // Replace with the actual token key if different
        const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8080";
        console.log("Using API URL:", apiUrl);
        
        const response = await fetch(`${apiUrl}/api/csv/dashboard`, {
              headers: {
                    Authorization: `Bearer ${token}`, // Use the Bearer token for authorization
                    "Content-Type": "application/json", // Optional, but good practice
                  },
        });

        if (response.ok) {
          const { dashboard } = await response.json();

          // Update charts only if data exists
          if (dashboard.line_chart) setLineChartData(dashboard.line_chart);
          if (dashboard.bar_chart) setBarChartData(dashboard.bar_chart);
          if (dashboard.pie_chart) setPieChartData(dashboard.pie_chart);
          if (dashboard.map_data) setMapMarkers(dashboard.map_data);
        } else {
          console.error("Failed to fetch dashboard data");
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      }
    };

    fetchDashboardData();
  }, []);

  const handleFilterChange = (filterType, value) => {
    switch (filterType) {
      case "region":
        setRegionFilter(value);
        break;
      case "productCategory":
        setProductCategoryFilter(value);
        break;
      case "timeRange":
        setTimeRange(value);
        if (value === "last30") {
          const endDate = new Date();
          const startDate = new Date();
          startDate.setDate(endDate.getDate() - 30);
          setDateRange({
            start: startDate.toISOString().split("T")[0],
            end: endDate.toISOString().split("T")[0],
          });
        } else if (value === "last60") {
          const endDate = new Date();
          const startDate = new Date();
          startDate.setDate(endDate.getDate() - 60);
          setDateRange({
            start: startDate.toISOString().split("T")[0],
            end: endDate.toISOString().split("T")[0],
          });
        } else if (value === "last90") {
          const endDate = new Date();
          const startDate = new Date();
          startDate.setDate(endDate.getDate() - 90);
          setDateRange({
            start: startDate.toISOString().split("T")[0],
            end: endDate.toISOString().split("T")[0],
          });
        }
        break;
      case "dateRange":
        setDateRange(value);
        setTimeRange("custom");
        break;
      default:
        break;
    }
  };

  return (
    <Box sx={{ marginTop: 3 }}>
      <Grid container spacing={3}>
        {/* Filters */}
        <Grid item xs={12}>
          <FormControl sx={{ minWidth: 120, marginRight: 2 }}>
            <Select
              value={timeRange}
              onChange={(e) => handleFilterChange("timeRange", e.target.value)}
              displayEmpty
            >
              <MenuItem value="custom">Custom Range</MenuItem>
              <MenuItem value="last30">Last 30 Days</MenuItem>
              <MenuItem value="last60">Last 60 Days</MenuItem>
              <MenuItem value="last90">Last 90 Days</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 120, marginRight: 2 }}>
            <Select
              value={regionFilter}
              onChange={(e) => handleFilterChange("region", e.target.value)}
              displayEmpty
            >
              <MenuItem value="all">All Regions</MenuItem>
              <MenuItem value="NA">North America</MenuItem>
              <MenuItem value="EU">Europe</MenuItem>
              <MenuItem value="APAC">Asia Pacific</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 120, marginRight: 2 }}>
            <Select
              value={productCategoryFilter}
              onChange={(e) => handleFilterChange("productCategory", e.target.value)}
              displayEmpty
            >
              <MenuItem value="all">All Products</MenuItem>
              <MenuItem value="electronics">Electronics</MenuItem>
              <MenuItem value="clothing">Clothing</MenuItem>
              <MenuItem value="furniture">Furniture</MenuItem>
            </Select>
          </FormControl>
          <TextField
            type="date"
            label="Start Date"
            value={dateRange.start}
            onChange={(e) => handleFilterChange("dateRange", { ...dateRange, start: e.target.value })}
            sx={{ marginRight: 2 }}
          />
          <TextField
            type="date"
            label="End Date"
            value={dateRange.end}
            onChange={(e) => handleFilterChange("dateRange", { ...dateRange, end: e.target.value })}
          />
        </Grid>

        {/* Line Chart */}
        <Grid item xs={12} md={6}>
          <Paper elevation={5} sx={{ padding: 2, borderRadius: 3 }}>
            <Typography variant="h6" gutterBottom>
              Revenue Over Time
            </Typography>
            <Box sx={{ height: 250 }}>
              <Line data={lineChartData} options={{ responsive: true }} />
            </Box>
          </Paper>
        </Grid>

        {/* Bar Chart */}
        <Grid item xs={12} md={6}>
          <Paper elevation={5} sx={{ padding: 2, borderRadius: 3 }}>
            <Typography variant="h6" gutterBottom>
              Sales by Product
            </Typography>
            <Box sx={{ height: 250 }}>
              <Bar data={barChartData} options={{ responsive: true }} />
            </Box>
          </Paper>
        </Grid>

        {/* Pie Chart */}
        <Grid item xs={6}>
          <Paper elevation={5} sx={{ padding: 2, borderRadius: 3 }}>
            <Typography variant="h6" gutterBottom>
              Customer Segmentation
            </Typography>
            <Box sx={{ height: 250 }}>
              <Pie data={pieChartData} options={{ responsive: true }} />
            </Box>
          </Paper>
        </Grid>

        {/* Map */}
        <Grid item xs={12}>
          <Paper elevation={5} sx={{ padding: 2, borderRadius: 3 }}>
            <Typography variant="h6" gutterBottom>
              Revenue by Location
            </Typography>
            <Box sx={{ height: 350 }}>
              <MapContainer center={[37.7749, -122.4194]} zoom={4} style={{ height: "100%", width: "100%" }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                {mapMarkers.map((marker, index) => (
                  <Marker key={index} position={marker.position}>
                    <Popup>{marker.label}</Popup>
                  </Marker>
                ))}
              </MapContainer>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardCharts;
